import React from "react";
import { Sparkles, X } from "lucide-react";

/**
 * StudioInlineBanner — sticky contextual banner shown right under the top chrome
 * when the user navigates to a section that still has unanswered questions.
 *
 * Props:
 *   question  — full question object from BLOCKS (label, type, options, helper)
 *   onAnswer  — (value) => void  (debounced auto-save handled by parent)
 *   onSkip    — ()       => void  (dismiss without saving)
 *   bannerKey — unique key for resetting local UI state when the banner switches
 */
export default function StudioInlineBanner({ question, onAnswer, onSkip, bannerKey }) {
  if (!question) return null;

  const handleOption = (opt) => {
    if (question.type === "checkbox") {
      // The parent will merge with current selection — here we just toggle one value.
      onAnswer({ toggle: opt });
    } else {
      onAnswer({ value: opt });
    }
  };

  return (
    <div
      key={bannerKey}
      className="sticky top-12 z-30 shadow-[0_4px_18px_rgba(15,43,91,0.10)]"
      style={{
        background: "#ffffff",
        borderLeft: "3px solid #4FC3F7",
        border: "0.5px solid #BAE6FD",
        borderLeftWidth: 3,
        borderRadius: "0 8px 8px 0",
      }}
      data-testid="studio-inline-banner"
      data-question-id={question.id}
    >
      <div className="px-4 lg:px-6 py-3 flex items-start gap-3 max-w-[1600px] mx-auto">
        <Sparkles size={16} style={{ color: "#4FC3F7" }} className="mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-medium leading-snug" style={{ color: "#0F2B5B" }} data-testid="banner-label">
            {question.label}
          </div>
          {question.helper && (
            <p className="text-[12px] mt-0.5 leading-snug italic" style={{ color: "#64748B" }} data-testid="banner-helper">
              {question.helper}
            </p>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {question.type === "radio" &&
              (question.options || []).map((opt) => (
                <button
                  key={opt}
                  onClick={() => handleOption(opt)}
                  data-testid={`banner-option-${opt.replace(/\s+/g, "-")}`}
                  className="text-[12px] px-4 py-1.5 rounded-full transition-colors"
                  style={{
                    background: "#ffffff",
                    color: "#64748B",
                    border: "0.5px solid #CBD5E0",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#F0F9FF"; e.currentTarget.style.borderColor = "#4FC3F7"; e.currentTarget.style.color = "#0F2B5B"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#ffffff"; e.currentTarget.style.borderColor = "#CBD5E0"; e.currentTarget.style.color = "#64748B"; }}
                >
                  {opt}
                </button>
              ))}
            {question.type === "checkbox" &&
              (question.options || []).map((opt) => (
                <button
                  key={opt}
                  onClick={() => handleOption(opt)}
                  data-testid={`banner-option-${opt.replace(/\s+/g, "-")}`}
                  className="text-[12px] px-4 py-1.5 rounded-full transition-colors"
                  style={{
                    background: "#ffffff",
                    color: "#64748B",
                    border: "0.5px solid #CBD5E0",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#F0F9FF"; e.currentTarget.style.borderColor = "#4FC3F7"; e.currentTarget.style.color = "#0F2B5B"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#ffffff"; e.currentTarget.style.borderColor = "#CBD5E0"; e.currentTarget.style.color = "#64748B"; }}
                >
                  + {opt}
                </button>
              ))}
            {question.type === "text" && (
              <InlineTextInput placeholder={question.label} onSubmit={(value) => onAnswer({ value })} testid={`banner-input-${question.id}`} />
            )}
            {question.type === "textarea" && (
              <InlineTextInput placeholder={question.label} multiline onSubmit={(value) => onAnswer({ value })} testid={`banner-input-${question.id}`} />
            )}
          </div>
        </div>

        <button
          onClick={onSkip}
          data-testid="banner-skip"
          className="text-[12px] inline-flex items-center gap-1 shrink-0 transition"
          style={{ color: "#94A3B8" }}
          onMouseEnter={(e) => { e.currentTarget.style.color = "#0F2B5B"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = "#94A3B8"; }}
          title="Passer cette question"
        >
          Passer <X size={12} />
        </button>
      </div>
    </div>
  );
}

function InlineTextInput({ placeholder, multiline, onSubmit, testid }) {
  const [v, setV] = React.useState("");
  const submit = () => {
    const trimmed = v.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
    setV("");
  };
  return (
    <div className="flex items-center gap-1.5 w-full max-w-md">
      <input
        type="text"
        value={v}
        onChange={(e) => setV(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        onKeyDown={(e) => { if (e.key === "Enter" && !multiline) submit(); }}
        className="flex-1 text-[12.5px] px-3 py-2 rounded-md focus:outline-none transition-shadow"
        style={{
          background: "#ffffff",
          border: "0.5px solid #CBD5E0",
          color: "#0F2B5B",
          borderRadius: 8,
        }}
        onFocus={(e) => { e.currentTarget.style.borderColor = "#4FC3F7"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(79,195,247,0.15)"; }}
        onBlur={(e) => { e.currentTarget.style.borderColor = "#CBD5E0"; e.currentTarget.style.boxShadow = "none"; }}
      />
      <button
        onClick={submit}
        className="text-[12px] px-3 py-2 font-semibold hover:brightness-110 transition"
        style={{ background: "#0F2B5B", color: "#ffffff", borderRadius: 8 }}
      >
        OK
      </button>
    </div>
  );
}
